import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { SIZE_UNITS, validatorMaxFileSize, validatorMinFileSize } from '@emma-helpers/emma-validators.helper';
import { getHumanReadableFileSize, isFileSizeValid } from '@emma-helpers/file.helper';
import { ToastNotificationsService } from '@platform/services/toast-notifications.service';
import { noop } from 'lodash';

import { EMMAFormElementComponent } from './emma-form-element.component';
import { getFileSizeError } from './emma-input-file.i18n';

const VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAInputFileComponent),
  multi: true,
};

const VALIDATOR: any = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAInputFileComponent),
  multi: true,
};

@Component({
    selector: 'emma-input-file',
    templateUrl: './emma-input-file.component.html',
    providers: [
        VALUE_ACCESSOR,
        VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMAInputFileComponent,
        },
    ],
    standalone: false
})
export class EMMAInputFileComponent
  extends EMMAFormElementComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  @Input() fileErrorText = $localize`Error cargando fichero`;
  @Input() fileLoadingText = $localize`Cargando...`;
  @Input() override placeholder = $localize`Selecciona un fichero`;
  @Input() accept = '*';
  @Input() clearable = true;
  @Input() maxSize = Infinity;
  @Input() maxSizeUnit = SIZE_UNITS.MB;
  @Input() minSize = 0;
  @Input() minSizeUnit = SIZE_UNITS.KB;
  @Input() fileId = `emma-input-file__${Math.random()}`;
  @Input() inputClass = '';
  _inputClass: Array<string> = [];
  @Input() fileValidator?: (file: File) => Promise<void>;
  @Output() override $change = new EventEmitter<File | null>();
  @Output() override $blur = new EventEmitter<File | null>();

  hasError = false;
  filename = '';
  @Output() filenameChange = new EventEmitter<string>();
  size = 0;
  humanReadableSize = getHumanReadableFileSize(0);
  @Output() sizeChange = new EventEmitter<number>();

  // Form
  @Input()
  get file(): File | null {
    return this._innerValue;
  }
  set file(file: File | null) {
    if (file !== this._innerValue) {
      this._innerValue = file;
      this.size = file?.size ?? 0;
      this.humanReadableSize = getHumanReadableFileSize(this.size);
      this.filename = file?.name ?? '';
      this.onChangeCallback(this._innerValue);
      this.$change.emit(this._innerValue);
      this.sizeChange.emit(this.size);
      this.filenameChange.emit(this.filename);
    }
  }
  private _innerValue: File | null = null;
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: File | null) => void = noop;

  constructor(public notifications: ToastNotificationsService) {
    super();
  }

  override ngOnInit(): void {
    this.updateClasses();
  }
  override ngOnChanges(): void {
    this.updateClasses();
  }

  // Set touched on blur
  onBlur(): void {
    this.onTouchedCallback();
    this.$blur.emit(this.file);
  }
  onClear(): void {
    this.file = null;
    this.hasError = false;
    this.updateClasses();
  }

  onChange(event: Event): void {
    // Clear
    this.file = null;
    this.hasError = false;
    // Parse
    const target: any = event.target;
    if (target) {
      const file: File = target.files[0];
      if (file) {
        this.validateFile(file)
          .then(() => {
            this.file = file;
            this.updateClasses();
          })
          .catch((error: Error) => {
            this.file = null;
            this.hasError = true;
            this.fileErrorText = error.message;
            this.updateClasses();
          });
      }
    }
    this.updateClasses();
  }

  private isAllowedSize(size: number) {
    return isFileSizeValid({
      maxSize: this.maxSize,
      minSize: this.minSize,
      maxSizeUnit: this.maxSizeUnit,
      minSizeUnit: this.minSizeUnit,
    })(size);
  }

  private validateFile(inputFile: File): Promise<void> {
    return Promise.resolve(inputFile).then((file) => {
      if (!this.isAllowedSize(file.size)) {
        throw new Error(
          getFileSizeError(
            file.size,
            this.minSize ? this.minSize * this.minSizeUnit : 0,
            this.maxSize < Infinity ? this.maxSize * this.maxSizeUnit : Infinity
          )
        );
      }
      if (this.fileValidator) {
        return this.fileValidator(file).catch(() => {
          throw new Error($localize`El formato o el contenido del fichero no es válido.`);
        });
      } else {
        return;
      }
    });
  }
  override updateClasses(): void {
    const containerClasses = ['emma-input-file', this.containerClass];
    this._containerClass = containerClasses;

    const inputClasses = [
      'emma-input-file__label',
      'm-input--solid',
      'emma-input__input',
      'form-control',
      'm-input',
      this.inputClass,
    ];
    if (this.hasError) {
      inputClasses.push('m-input--ko');
    } else if (this._innerValue) {
      inputClasses.push('m-input--ok');
    }
    this._inputClass = inputClasses;
  }

  // BEGIN From ControlValueAccessor interface
  writeValue(file?: File | null): void {
    if (file !== this._innerValue) {
      this._innerValue = file ?? null;
      this.size = file?.size ?? 0;
      this.humanReadableSize = getHumanReadableFileSize(this.size);
      this.filename = file?.name ?? '';
      this.hasError = false;
    }
    this.updateClasses();
  }
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }
  // END From ControlValueAccessor interface

  // From Validator interface
  override validate: ValidatorFn = (control: AbstractControl) => {
    const validators: ValidatorFn[] = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.maxSize < Infinity) {
      validators.push(validatorMaxFileSize(this.maxSize, this.maxSizeUnit));
    }
    if (this.minSize) {
      validators.push(validatorMinFileSize(this.minSize, this.minSizeUnit));
    }
    if (this.validateFn) {
      validators.push(this.validateFn);
    }
    if (validators.length) {
      const validator = Validators.compose(validators);
      return validator ? validator(control) : null;
    }
    return null;
  };
}
